import React from 'react';
import { MapPin, Mail, Phone } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from '../Context/TranslationContext';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const Contact = () => {
  const { isTamil } = useTranslation();
  const position = [13.0555367, 80.2814119]; // Coordinates for Netaji Statue, Triplicane

  const translations = {
    en: {
      title: 'Contact Us',
      address: 'Address',
      addressLine1: 'The Bharat Scouts and Guides, Tamil Nadu',
      addressLine2: 'State Headquarters,Wenlock Park, Triplicane',
      addressLine3: 'Chennai, Tamil Nadu 600005',
      website: 'Email',
      websiteUrl: 'bsgtamilnadu@gmail.com',
      phone: 'Phone',
      phoneNumber: '044 2844 2027',
      findUs: 'Find Us Here',
      operatingHours: 'Operating Hours',
      hours: 'Monday to Saturday: 9:00 AM - 6:00 PM',
      closed: 'Sunday: Closed'
    },
    ta: {
      title: 'எங்களை தொடர்பு கொள்ள',
      address: 'முகவரி',
      addressLine1: 'நேதாஜி சிலைக்கு எதிரில், காமராஜர் சாலை',
      addressLine2: 'வென்லாக் பூங்கா, திருவல்லிக்கேணி',
      addressLine3: 'சென்னை, தமிழ்நாடு 600005',
      website: 'மின்னஞ்சல்',
  
      websiteUrl: 'bsgtamilnadu@gmail.com',
      phone: 'தொலைபேசி',
      phoneNumber: '044 2844 2027',
      findUs: 'எங்களை இங்கே காணலாம்',
      operatingHours: 'இயக்க நேரம்',
      hours: 'திங்கள் முதல் சனி: காலை 9:00 - மாலை 6:00',
      closed: 'ஞாயிறு: விடுமுறை'
    }
  };

  const t = translations[isTamil ? 'ta' : 'en'];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-32 pb-20 mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h1
          className="text-4xl font-bold text-[#1A2E44] mb-4 text-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {t.title}
        </motion.h1>
        
        <motion.div
          className="w-20 h-1 bg-[#5f81e0] mx-auto mb-12 rounded-full"
          initial={{ width: 0 }}
          animate={{ width: 80 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Information */}
          <motion.div
            className="bg-white rounded-xl shadow-lg p-8 space-y-8 hover:shadow-xl transition-shadow duration-300"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="space-y-6">
              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition-colors duration-300">
                  <MapPin className="w-6 h-6 text-[#5f81e0]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">{t.address}</h3>
                  <p className="text-gray-600 leading-relaxed">{t.addressLine1}</p>
                  <p className="text-gray-600 leading-relaxed">{t.addressLine2}</p>
                  <p className="text-gray-600 leading-relaxed">{t.addressLine3}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition-colors duration-300">
                  <Mail className="w-6 h-6 text-[#5f81e0]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">{t.website}</h3>
                  <a 
                    href={`https://${t.websiteUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-[#5f81e0] transition-colors duration-300"
                  >
                    {t.websiteUrl}
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition-colors duration-300">
                  <Phone className="w-6 h-6 text-[#5f81e0]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">{t.phone}</h3>
                  <a 
                    href={`tel:${t.phoneNumber.replace(/\s/g, '')}`}
                    className="text-gray-600 hover:text-[#5f81e0] transition-colors duration-300"
                  >
                    {t.phoneNumber}
                  </a>
                </div>
              </div>

              <div className="pt-6 border-t border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-3 text-lg">{t.operatingHours}</h3>
                <p className="text-gray-600 leading-relaxed">{t.hours}</p>
                <p className="text-gray-600 leading-relaxed mt-1">{t.closed}</p>
              </div>
            </div>
          </motion.div>

          {/* Map */}
          <motion.div
            className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-900">{t.findUs}</h2>
            </div>
            <div className="h-[400px]">
              <MapContainer 
                center={position} 
                zoom={15}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={false}
                dragging={true}
                zoomControl={true}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <Marker position={position}>
                  <Popup>
                    <div className="font-semibold">TNBSG</div>
                    <div className="text-sm">{t.addressLine1}</div>
                    <div className="text-sm">{t.addressLine2}</div>
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;